"use client";

// Typed interface for MuxPlayer element — covers all the private APIs we poke at
interface MuxPlayerElement extends HTMLElement {
  play?: () => Promise<void>;
  pause?: () => void;
  muted?: (() => boolean) | boolean;
  volume?: ((v?: number) => void) | number;
  setVolume?: (v: number) => void;
  getVideoElement?: () => HTMLVideoElement | null;
  shadowRoot: ShadowRoot | null;
  media?: HTMLVideoElement | null;
}

import { isInstagramBrowser } from "@/lib/instagramBrowserDetection";
import { BRAND_SOURCE_KEY } from "@/lib/brandConfig";
import MuxPlayer from "@mux/mux-player-react";
import { ExternalLink } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

// Hook to detect mobile - Initialize correctly to prevent layout shift
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.innerWidth < 768;
  });

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  return isMobile;
};

// Display mode types
export type VideoDisplayMode = "reel" | "contain" | "cover" | "auto";

// Action button configuration
export interface VideoActionButton {
  label: string; // Button text
  url: string; // URL to navigate to
  openInNewTab?: boolean; // Open in new tab (default: false)
  className?: string; // Optional custom CSS classes
}

/** `new URL("/path")` lanza; las rutas internas del admin/BaseTemplate son relativas. */
function isMuxVideoActionUrlAllowed(url: string): boolean {
  const u = url.trim();
  if (!u) return false;
  if (u.startsWith("/") && !u.startsWith("//")) return true;
  try {
    const parsed = new URL(u);
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
}

interface MuxVideoPlayerProps {
  playbackId?: string; // Mux playback ID (required for videos)
  cloudflareId?: string; // Legacy support - will be converted to playbackId if playbackId not provided
  title?: string;
  onReady?: () => void;
  onPlay?: () => void;
  onEnded?: () => void;
  /** Called when playback reaches 25%, 50%, 75%, or 100% for progress tracking */
  onProgress?: (percent: number, currentTimeSeconds: number) => void;
  isVisible?: boolean;

  // Display configuration - simplified and more intuitive
  displayMode?: VideoDisplayMode; // 'reel' | 'contain' | 'cover' | 'auto' (default: 'auto')
  // 'reel' = Instagram-style full screen, 'contain' = fit with borders, 'cover' = fill screen, 'auto' = reel on mobile, contain on desktop

  // Container styling
  className?: string;
  containerClassName?: string;

  // Action button
  actionButton?: VideoActionButton;

  // Player options - simplified defaults for gallery/reel mode
  autoplay?: boolean | "muted"; // Default: 'muted' for gallery
  loop?: boolean; // Default: true for gallery
  muted?: boolean; // Default: true for gallery
  controls?: boolean; // Default: false for gallery (tap to mute)

  // Mux Data configuration (optional)
  envKey?: string; // Mux Data environment key (if different from default)
  metadata?: Record<string, string | number>; // Additional metadata for Mux Data tracking

  // Callbacks
  onActionClick?: (url: string) => void;
}

const MuxVideoPlayer: React.FC<MuxVideoPlayerProps> = ({
  playbackId,
  cloudflareId, // Legacy support
  title,
  onReady,
  onPlay,
  onEnded,
  onProgress,
  isVisible = true,
  displayMode = "auto", // Default: auto-detects mobile/desktop
  className = "",
  containerClassName = "",
  actionButton,
  autoplay = "muted", // Default: muted autoplay for reel-style experience
  loop = true, // Default: loop for gallery
  muted = true, // Default: muted for gallery (tap to unmute)
  controls = false, // Default: no controls for gallery (tap to mute)
  envKey, // Optional Mux Data env key
  metadata: customMetadata, // Optional custom metadata
  onActionClick,
}) => {
  const playerRef = useRef<MuxPlayerElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(muted);
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const isMobile = useIsMobile();
  const isInstagram = isInstagramBrowser();

  // Touch gesture state for Instagram-like interactions
  const touchStartRef = useRef<{ x: number; y: number; time: number } | null>(
    null,
  );
  const progressMilestonesRef = useRef<Set<number>>(new Set());

  // Use playbackId (primary) or convert cloudflareId (legacy support)
  // Mux replaces Cloudflare Video completely
  const muxPlaybackId = playbackId || cloudflareId || "";

  // Reset error state and progress milestones when playbackId changes
  useEffect(() => {
    if (muxPlaybackId) {
      setHasError(false);
      setErrorMessage("");
      progressMilestonesRef.current = new Set();
    }
  }, [muxPlaybackId]);

  // Determine display mode - simplified logic
  const getDisplayMode = (): "reel" | "contain" | "cover" => {
    if (displayMode === "auto") {
      // Auto: reel on mobile, contain on desktop
      return isMobile ? "reel" : "contain";
    }
    // Use explicit displayMode if provided
    return displayMode as "reel" | "contain" | "cover";
  };

  const currentDisplayMode = getDisplayMode();
  const isReelMode = currentDisplayMode === "reel";

  // Sync muted state with player
  useEffect(() => {
    const player = playerRef.current;
    if (player && typeof player.muted === "function") {
      player.muted(isMuted);
    }
  }, [isMuted]);

  // Set volume to 0.5 (medium) when video loads and is not muted
  useEffect(() => {
    if (!muted && isVisible) {
      const setVolume = () => {
        const player = playerRef.current;
        if (!player) return;

        // Try different methods to set volume (MuxPlayer API may vary)
        try {
          // Method 1: Access video element via shadow DOM or direct access
          let videoElement: HTMLVideoElement | null = null;

          // Try to get video element from MuxPlayer
          if ((player as MuxPlayerElement).querySelector) {
            videoElement = (player as MuxPlayerElement).querySelector("video");
          }

          // Try shadow DOM access
          if (!videoElement && (player as MuxPlayerElement).shadowRoot) {
            videoElement = (player as MuxPlayerElement).shadowRoot.querySelector("video");
          }

          // Try media property
          if (!videoElement && (player as MuxPlayerElement).media) {
            videoElement = (player as MuxPlayerElement).media;
          }

          // Try getVideoElement method if available
          if (
            !videoElement &&
            typeof (player as MuxPlayerElement).getVideoElement === "function"
          ) {
            videoElement = (player as MuxPlayerElement).getVideoElement();
          }

          // Set volume on video element if found
          if (videoElement && "volume" in videoElement) {
            videoElement.volume = 0.5;
            return;
          }

          // Method 2: Direct property access on player
          if (
            "volume" in player &&
            typeof (player as MuxPlayerElement).volume === "number"
          ) {
            (player as MuxPlayerElement).volume = 0.5;
            return;
          }

          // Method 3: Function call
          if (typeof (player as MuxPlayerElement).volume === "function") {
            (player as MuxPlayerElement).volume(0.5);
            return;
          }

          // Method 4: setVolume method
          if (typeof (player as MuxPlayerElement).setVolume === "function") {
            (player as MuxPlayerElement).setVolume(0.5);
            return;
          }
        } catch (error) {
          // Silently fail if volume can't be set
          console.debug("Could not set volume:", error);
        }
      };

      // Try immediately
      setVolume();

      // Try after delays to ensure player is ready
      const timeouts = [
        setTimeout(setVolume, 100),
        setTimeout(setVolume, 300),
        setTimeout(setVolume, 500),
        setTimeout(setVolume, 1000),
        setTimeout(setVolume, 2000),
      ];

      return () => {
        timeouts.forEach((timeout) => clearTimeout(timeout));
      };
    }
  }, [muted, isVisible, isPlaying]);

  // Handle play/pause visibility changes - Reel-style autoplay
  useEffect(() => {
    const player = playerRef.current;
    if (player && isVisible && autoplay) {
      // Try to play with sound if not muted
      const tryPlay = async () => {
        try {
          // First ensure volume is set if not muted
          if (!muted) {
            let videoElement: HTMLVideoElement | null = null;

            if ((player as MuxPlayerElement).querySelector) {
              videoElement = (player as MuxPlayerElement).querySelector("video");
            }

            if (!videoElement && (player as MuxPlayerElement).shadowRoot) {
              videoElement = (player as MuxPlayerElement).shadowRoot.querySelector("video");
            }

            if (!videoElement && (player as MuxPlayerElement).media) {
              videoElement = (player as MuxPlayerElement).media;
            }

            if (videoElement && "volume" in videoElement) {
              videoElement.volume = 0.5;
            }
          }

          // Then try to play
          if (typeof player.play === "function") {
            await player.play();
          }
        } catch (error) {
          // Autoplay with sound may fail due to browser policies
          // That's okay, user can manually play
          console.debug(
            "Autoplay failed (may require user interaction):",
            error,
          );
        }
      };

      // Try immediately
      tryPlay();

      // Also try after a short delay in case player isn't ready
      const timeout = setTimeout(tryPlay, 100);

      return () => clearTimeout(timeout);
    } else if (player && !isVisible) {
      // Pause when not visible
      if (typeof player.pause === "function") {
        player.pause();
      }
    }
  }, [isVisible, autoplay, muted]);

  const handlePlay = useCallback(() => {
    setIsPlaying(true);

    // Ensure volume is set to 0.5 when video starts playing
    if (!muted && isVisible) {
      const player = playerRef.current;
      if (player) {
        try {
          // Try to get video element
          let videoElement: HTMLVideoElement | null = null;

          if ((player as MuxPlayerElement).querySelector) {
            videoElement = (player as MuxPlayerElement).querySelector("video");
          }

          if (!videoElement && (player as MuxPlayerElement).shadowRoot) {
            videoElement = (player as MuxPlayerElement).shadowRoot.querySelector("video");
          }

          if (!videoElement && (player as MuxPlayerElement).media) {
            videoElement = (player as MuxPlayerElement).media;
          }

          if (
            !videoElement &&
            typeof (player as MuxPlayerElement).getVideoElement === "function"
          ) {
            videoElement = (player as MuxPlayerElement).getVideoElement();
          }

          // Set volume on video element if found
          if (videoElement && "volume" in videoElement) {
            videoElement.volume = 0.5;
          } else if (
            "volume" in player &&
            typeof (player as MuxPlayerElement).volume === "number"
          ) {
            (player as MuxPlayerElement).volume = 0.5;
          } else if (typeof (player as MuxPlayerElement).volume === "function") {
            (player as MuxPlayerElement).volume(0.5);
          } else if (typeof (player as MuxPlayerElement).setVolume === "function") {
            (player as MuxPlayerElement).setVolume(0.5);
          }
        } catch (error) {
          console.debug("Could not set volume on play:", error);
        }
      }
    }

    onPlay?.();
  }, [onPlay, muted, isVisible]);

  const handlePause = useCallback(() => {
    setIsPlaying(false);
  }, []);

  const handleEnded = useCallback(() => {
    setIsPlaying(false);
    onEnded?.();
  }, [onEnded]);

  const handleTimeUpdate = useCallback(() => {
    if (!onProgress) return;
    const player = playerRef.current;
    if (!player) return;
    const el = player as unknown as { currentTime?: number; duration?: number };
    const currentTime = el?.currentTime ?? 0;
    const duration = el?.duration ?? 0;
    if (!duration || duration <= 0) return;
    const percent = Math.floor((currentTime / duration) * 100);
    for (const milestone of [25, 50, 75, 100]) {
      if (percent >= milestone && !progressMilestonesRef.current.has(milestone)) {
        progressMilestonesRef.current.add(milestone);
        onProgress(milestone, currentTime);
      }
    }
  }, [onProgress]);

  // Handle mute state changes from player
  const handleVolumeChange = useCallback(() => {
    const player = playerRef.current;
    if (player && typeof player.muted === "function") {
      setIsMuted(player.muted());
    }
  }, []);

  // Toggle mute/unmute (Instagram-like tap to mute)
  const toggleMute = useCallback(() => {
    const player = playerRef.current;
    if (player) {
      if (typeof player.muted === "function") {
        const newMutedState = !isMuted;
        player.muted(newMutedState);
        setIsMuted(newMutedState);
      }
    }
  }, [isMuted]);

  // Handle tap gesture for mute/unmute (Instagram-like: single tap to toggle)
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    const touch = e.touches[0];
    touchStartRef.current = {
      x: touch.clientX,
      y: touch.clientY,
      time: Date.now(),
    };
  }, []);

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      if (!touchStartRef.current) return;

      const touch = e.changedTouches[0];
      const deltaX = Math.abs(touch.clientX - touchStartRef.current.x);
      const deltaY = Math.abs(touch.clientY - touchStartRef.current.y);
      const deltaTime = Date.now() - touchStartRef.current.time;

      // If it's a tap (small movement, short time) and not a swipe
      // Instagram-like: single tap toggles mute/unmute
      if (deltaX < 10 && deltaY < 10 && deltaTime < 300) {
        // Single tap - toggle mute (Instagram behavior)
        toggleMute();
      }

      touchStartRef.current = null;
    },
    [toggleMute],
  );

  const handleError = useCallback(
    (error?: Record<string, unknown>) => {
      // Check if it's a 404 or invalid playbackId error
      const errorMessage = error?.message || "";
      const errorType = error?.type || "";
      const errorDetails = error?.details || "";

      // Detect invalid playbackId (404 errors from Mux)
      const isInvalidPlaybackId =
        errorMessage.includes("does not exist") ||
        errorMessage.includes("404") ||
        errorType === "networkError" ||
        errorDetails === "manifestLoadError" ||
        (error?.url &&
          error.url.includes("stream.mux.com") &&
          error.url.includes("404"));

      if (isInvalidPlaybackId) {
        setHasError(true);
        setErrorMessage(
          "This video is not available. The playback ID may be invalid or the video has been removed.",
        );
      } else {
        setHasError(true);
        setErrorMessage("Video could not be loaded. Please try again later.");
      }

      // Log error for debugging but don't throw
      console.debug("MuxVideoPlayer error:", {
        playbackId: muxPlaybackId,
        error: error || "Unknown error",
        isInvalidPlaybackId,
      });
    },
    [muxPlaybackId],
  );

  // Listen for errors directly from video element (catches errors MuxPlayer might not pass)
  useEffect(() => {
    const player = playerRef.current;
    if (!player || !muxPlaybackId) return;

    const handleVideoError = (event: Event) => {
      const error = (event.target as HTMLVideoElement)?.error;
      if (error) {
        handleError({
          type: "mediaError",
          message: `Video error: ${error.message || "Unknown error"}`,
          code: error.code,
          details: `Code ${error.code}: ${error.message || "Unknown error"}`,
        });
      } else {
        handleError({
          type: "networkError",
          message: "Video could not be loaded",
          details: "Network or loading error",
        });
      }
    };

    // Try to get video element and listen for errors
    const setupErrorListener = () => {
      let videoElement: HTMLVideoElement | null = null;

      if ((player as MuxPlayerElement).querySelector) {
        videoElement = (player as MuxPlayerElement).querySelector("video");
      }

      if (!videoElement && (player as MuxPlayerElement).shadowRoot) {
        videoElement = (player as MuxPlayerElement).shadowRoot.querySelector("video");
      }

      if (!videoElement && (player as MuxPlayerElement).media) {
        videoElement = (player as MuxPlayerElement).media;
      }

      if (videoElement) {
        videoElement.addEventListener("error", handleVideoError);
        return () => {
          videoElement?.removeEventListener("error", handleVideoError);
        };
      }
    };

    // Try immediately and after delays
    const cleanup1 = setupErrorListener();
    const timeout1 = setTimeout(() => {
      const cleanup2 = setupErrorListener();
      if (cleanup2) {
        setTimeout(() => cleanup2(), 5000);
      }
    }, 500);

    return () => {
      cleanup1?.();
      clearTimeout(timeout1);
    };
  }, [muxPlaybackId, handleError]);

  const handleLoadedMetadata = useCallback(() => {
    // Set volume to 0.5 when metadata is loaded (video is ready)
    if (!muted && isVisible) {
      const player = playerRef.current;
      if (player) {
        try {
          // Try to get video element
          let videoElement: HTMLVideoElement | null = null;

          if ((player as MuxPlayerElement).querySelector) {
            videoElement = (player as MuxPlayerElement).querySelector("video");
          }

          if (!videoElement && (player as MuxPlayerElement).shadowRoot) {
            videoElement = (player as MuxPlayerElement).shadowRoot.querySelector("video");
          }

          if (!videoElement && (player as MuxPlayerElement).media) {
            videoElement = (player as MuxPlayerElement).media;
          }

          if (
            !videoElement &&
            typeof (player as MuxPlayerElement).getVideoElement === "function"
          ) {
            videoElement = (player as MuxPlayerElement).getVideoElement();
          }

          // Set volume on video element if found
          if (videoElement && "volume" in videoElement) {
            videoElement.volume = 0.5;
          } else if (
            "volume" in player &&
            typeof (player as MuxPlayerElement).volume === "number"
          ) {
            (player as MuxPlayerElement).volume = 0.5;
          } else if (typeof (player as MuxPlayerElement).volume === "function") {
            (player as MuxPlayerElement).volume(0.5);
          } else if (typeof (player as MuxPlayerElement).setVolume === "function") {
            (player as MuxPlayerElement).setVolume(0.5);
          }
        } catch (error) {
          // Silently fail if volume can't be set
          console.debug("Could not set volume on metadata load:", error);
        }
      }
    }
    onReady?.();
  }, [onReady, muted, isVisible]);

  const handleActionClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();

      if (!actionButton || !actionButton.url) {
        return;
      }

      const targetUrl = actionButton.url.trim();
      if (!isMuxVideoActionUrlAllowed(targetUrl)) {
        return;
      }

      if (onActionClick) {
        onActionClick(actionButton.url);
        return;
      }

      const absoluteForNewTab =
        targetUrl.startsWith("/") && !targetUrl.startsWith("//")
          ? new URL(targetUrl, window.location.origin).href
          : targetUrl;

      if (actionButton.openInNewTab) {
        window.open(absoluteForNewTab, "_blank", "noopener,noreferrer");
      } else if (targetUrl.startsWith("/") && !targetUrl.startsWith("//")) {
        window.location.assign(targetUrl);
      } else {
        window.location.href = targetUrl;
      }
    },
    [actionButton, onActionClick],
  );

  // Container styling based on display mode - optimized for gallery/reel
  const getContainerClasses = () => {
    const baseClasses = "relative bg-black group";

    if (isReelMode) {
      if (isMobile) {
        // Mobile reel: full screen like Instagram
        const instagramClasses = isInstagram
          ? "instagram-browser-container"
          : "";
        return `${baseClasses} w-full h-screen max-h-screen overflow-hidden ${instagramClasses} ${containerClassName}`;
      } else {
        // Desktop reel: full height - video should reach bottom
        return `${baseClasses} w-full h-full min-h-screen flex items-start justify-center overflow-hidden ${containerClassName}`;
      }
    }

    // Contain mode: fit with borders
    if (currentDisplayMode === "contain") {
      return `${baseClasses} w-full h-full min-h-[400px] max-h-[90vh] flex items-center justify-center ${containerClassName}`;
    }

    // Cover mode: fill screen
    if (currentDisplayMode === "cover") {
      return `${baseClasses} w-full h-full min-h-[80vh] max-h-[90vh] ${containerClassName}`;
    }

    // Default
    return `${baseClasses} w-full h-full min-h-[400px] max-h-[800px] ${containerClassName}`;
  };

  // Get CSS styles based on display mode
  const getVideoStyles = () => {
    if (isReelMode) {
      if (isMobile) {
        return `
          .mux-video-player-${muxPlaybackId} {
            width: 100% !important;
            height: 100dvh !important;
            height: 100vh !important;
            max-height: 100dvh !important;
            max-height: 100vh !important;
            position: relative !important;
            overflow: hidden !important;
            -webkit-overflow-scrolling: touch !important;
          }
          .mux-video-player-${muxPlaybackId} mux-player {
            width: 100% !important;
            height: 100% !important;
            min-height: 100dvh !important;
            min-height: 100vh !important;
            max-height: 100dvh !important;
            max-height: 100vh !important;
            object-fit: cover !important;
            --controls: none !important;
            --media-object-fit: cover !important;
          }
          .mux-video-player-${muxPlaybackId} mux-player video {
            object-fit: cover !important;
            width: 100% !important;
            height: 100% !important;
          }
          @media (max-width: 767px) {
            .mux-video-player-${muxPlaybackId} {
              height: 100dvh !important;
              height: -webkit-fill-available !important;
              height: 100vh !important;
            }
          }
          ${
            isInstagram
              ? `
          .mux-video-player-${muxPlaybackId} {
            -webkit-overflow-scrolling: touch !important;
            touch-action: pan-y !important;
          }
          .mux-video-player-${muxPlaybackId} mux-player {
            -webkit-playsinline: true !important;
            playsinline: true !important;
          }
          .mux-video-player-${muxPlaybackId} button {
            -webkit-tap-highlight-color: transparent !important;
            touch-action: manipulation !important;
            min-height: 44px !important;
            min-width: 44px !important;
          }
          `
              : ""
          }
        `;
      } else {
        // Desktop reel: Full height display - video should reach bottom
        return `
          .mux-video-player-${muxPlaybackId} {
            width: 100% !important;
            height: 100% !important;
            min-height: 100vh !important;
            display: flex !important;
            align-items: flex-start !important;
            justify-content: center !important;
            background-color: #000 !important;
            position: relative !important;
            overflow: hidden !important;
          }
          .mux-video-player-${muxPlaybackId} mux-player {
            object-fit: contain !important;
            width: auto !important;
            height: 100% !important;
            max-width: min(56.25vh, 100%) !important;
            max-height: 100vh !important;
            min-width: 320px !important;
            margin: 0 auto !important;
            display: block !important;
            position: relative !important;
            --media-object-fit: contain !important;
            --controls: none !important;
          }
          .mux-video-player-${muxPlaybackId} mux-player video {
            object-fit: contain !important;
            width: 100% !important;
            height: 100% !important;
            max-width: 100% !important;
            max-height: 100% !important;
          }
          /* Better quality on desktop - no resolution cap */
          @media (min-width: 768px) {
            .mux-video-player-${muxPlaybackId} mux-player {
              max-width: min(56.25vh, 90vw) !important;
            }
          }
          /* Large desktop screens */
          @media (min-width: 1920px) {
            .mux-video-player-${muxPlaybackId} mux-player {
              max-width: min(56.25vh, 1200px) !important;
            }
          }
        `;
      }
    }

    if (currentDisplayMode === "cover") {
      return `
        .mux-video-player-${muxPlaybackId} mux-player {
          object-fit: cover !important;
          width: 100% !important;
          height: 100% !important;
        }
      `;
    }

    // Default contain mode - improved for desktop
    return `
      .mux-video-player-${muxPlaybackId} {
        width: 100% !important;
        height: 100% !important;
        min-height: 400px !important;
        max-height: 90vh !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        background-color: #000 !important;
      }
      .mux-video-player-${muxPlaybackId} mux-player {
        object-fit: contain !important;
        width: auto !important;
        height: auto !important;
        max-width: 100% !important;
        max-height: 90vh !important;
        margin: 0 auto !important;
        display: block !important;
        --media-object-fit: contain !important;
      }
      .mux-video-player-${muxPlaybackId} mux-player video {
        object-fit: contain !important;
        width: 100% !important;
        height: 100% !important;
      }
    `;
  };

  // Error state - Show error but allow navigation to continue
  if (hasError) {
    return (
      <div
        className={`relative w-full h-full bg-gradient-to-br from-gray-800 to-gray-900 flex items-center justify-center ${containerClassName}`}
      >
        <div className="text-center text-white p-6 sm:p-8 max-w-md mx-auto">
          <div className="w-16 h-16 mx-auto mb-4 bg-red-500/20 rounded-full flex items-center justify-center">
            <span className="text-red-400 text-2xl">⚠️</span>
          </div>
          {title && <h3 className="text-lg font-semibold mb-2">{title}</h3>}
          <p className="text-sm text-gray-300 mb-4">{errorMessage}</p>
          <p className="text-xs text-gray-400 mb-4">
            Use the arrows to navigate to another video.
          </p>
          <button
            onClick={() => {
              setHasError(false);
              setErrorMessage("");
            }}
            className="px-4 py-2 bg-gray-700/50 hover:bg-gray-700/70 text-white rounded-full text-sm font-medium transition-colors"
          >
            Dismiss
          </button>
        </div>
      </div>
    );
  }

  if (!muxPlaybackId) {
    return (
      <div
        className={`relative w-full h-full bg-black flex items-center justify-center ${containerClassName}`}
      >
        <div className="text-white text-sm">No playback ID provided</div>
      </div>
    );
  }

  return (
    <>
      {/* Custom styles for video player */}
      <style>{getVideoStyles()}</style>

      <div
        className={`${getContainerClasses()} mux-video-player-${muxPlaybackId} ${className}`}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {/* Mux Player */}
        <div
          className={`${
            isReelMode && !isMobile
              ? "w-full h-full flex items-start justify-center min-h-screen"
              : "w-full h-full flex items-center justify-center"
          }`}
        >
          <MuxPlayer
            ref={playerRef}
            streamType="on-demand"
            playbackId={muxPlaybackId}
            accentColor="#ac39f2"
            autoPlay={
              autoplay === true
                ? muted
                  ? "muted"
                  : true
                : autoplay === "muted"
                  ? "muted"
                  : false
            }
            loop={loop}
            muted={muted}
            playsInline
            {...(controls
              ? {
                  controls: true,
                }
              : {})}
            preload="metadata"
            onPlay={handlePlay}
            onPause={handlePause}
            onEnded={handleEnded}
            onTimeUpdate={handleTimeUpdate}
            onError={handleError}
            onLoadedMetadata={handleLoadedMetadata}
            onVolumeChange={handleVolumeChange}
            style={{
              width: "100%",
              height: "100%",
            }}
            // Mux Data integration - automatically tracks analytics
            // Mux Data is built-in when using playbackId, no additional SDK needed
            // Unlike video.js which requires videojs-mux plugin, MuxPlayer has native integration
            // See: https://mux.com/docs/guides/player-api-reference/react
            envKey={envKey} // Optional: override default env key from Mux dashboard
            metadata={{
              // High priority fields for Mux Data tracking
              video_id: muxPlaybackId, // Your internal video ID
              video_title: title || "", // Video title for analytics
              video_stream_type: "on-demand",
              // Regla de creador: etiqueta cada view con el brand (env Mux
              // compartido). Habilita scoping por `sub_property_id` en la Data API
              // (heatmaps/hotspots/exports — ver api/lib/muxData.ts). Un
              // customMetadata.sub_property_id explícito lo puede sobreescribir.
              sub_property_id: BRAND_SOURCE_KEY,
              // Merge with custom metadata if provided
              ...(customMetadata || {}),
            }}
            // Desktop: allow higher resolution, mobile: limit to 720p for better performance
            {...(isMobile
              ? {
                  capRenditionToPlayerSize: true,
                  maxAutoResolution: "720p",
                }
              : {})}
          />
        </div>

        {/* Video Title - Removed for reel-style experience */}

        {/* Mute indicator - Instagram-like visual feedback */}
        {isReelMode && isMobile && (
          <div
            className={`absolute top-4 right-4 z-20 transition-opacity duration-200 ${
              isMuted ? "opacity-100" : "opacity-0"
            }`}
          >
            <div className="bg-black/50 text-white px-3 py-1.5 rounded-full text-xs font-medium backdrop-blur-sm">
              🔇 Muted
            </div>
          </div>
        )}

        {/* Action Button */}
        {actionButton && actionButton.url && (
          <div className="absolute bottom-4 left-4 right-4 z-30 action-button-container">
            <button
              onClick={handleActionClick}
              className={`w-full bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 active:from-pink-700 active:to-purple-800 text-white font-semibold py-3 md:py-4 px-6 rounded-full transition-all duration-300 transform hover:scale-105 active:scale-95 shadow-lg hover:shadow-xl flex items-center justify-center gap-2 cursor-pointer focus:outline-none focus:ring-4 focus:ring-pink-500/50 disabled:opacity-50 disabled:cursor-not-allowed ${
                actionButton.className || ""
              }`}
              aria-label={`${actionButton.label}${actionButton.openInNewTab ? " (opens in new tab)" : ""}`}
            >
              <span className="text-sm md:text-base font-bold">
                {actionButton.label}
              </span>
              {actionButton.openInNewTab && (
                <ExternalLink className="w-4 h-4 md:w-5 md:h-5 flex-shrink-0" />
              )}
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default MuxVideoPlayer;
export type { MuxVideoPlayerProps };
